import CacheInstance from "./cache";

/**
 * Makes the groups sidebar (`aside.groups-sidebar`) horizontally resizable by
 * dragging a handle placed on its right edge.
 *
 * The sidebar's innerHTML is replaced whenever a groups manager re-renders, so
 * the handle is inserted as a *sibling* of the sidebar (inside the flex
 * container) rather than a child of it.
 *
 * The chosen width is persisted per page via CacheInstance so it survives
 * reloads and navigation.
 *
 * The width the user picked (`desiredWidth`) is kept separate from the width
 * currently applied: a narrow viewport clamps what's applied, but the desired
 * width is remembered so the sidebar springs back when the room returns.
 */

const DEFAULTS = {
  cacheKey: 'groupsSidebarWidth',
  defaultWidth: 300,
  minWidth: 50,
  maxWidth: 720,
  // never shrink the pane next to the sidebar below this
  contentMinWidth: 340,
  // below this the sidebar switches to a compact layout
  narrowWidth: 240,
  // below this it collapses to an icon rail (labels live in the tooltips)
  tinyWidth: 120,
  step: 16,
};

/**
 * Cache.save() stores the raw value in memory but wraps it as {timestamp, value}
 * in localStorage, so a read can come back in either shape.
 */
const readCachedWidth = key => {

  const cached = CacheInstance.get(key);

  if(cached === null || typeof cached === 'undefined') {
    return null;
  }

  const raw = (typeof cached === 'object' && 'value' in cached) ? cached.value : cached;
  const width = parseInt(raw, 10);

  return Number.isFinite(width) ? width : null;

};

class ResizableSidebar {

  constructor(options = {}) {

    this.settings = Object.assign({}, DEFAULTS, options);

    this.sidebar = this.settings.sidebar;

    if(!this.sidebar || !this.sidebar.parentElement) {
      return;
    }

    this.container = this.sidebar.parentElement;

    // what the user asked for, independent of what currently fits
    this.desiredWidth = Math.max(
      this.settings.minWidth,
      readCachedWidth(this.settings.cacheKey) || this.settings.defaultWidth
    );

    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onWindowResize = this._onWindowResize.bind(this);

    this._createHandle();
    this.applyWidth();

    window.addEventListener('resize', this._onWindowResize);

    // the container can be laid out (or change size) after we initialise
    if(window.ResizeObserver) {
      this._containerObserver = new ResizeObserver(() => this._scheduleApply());
      this._containerObserver.observe(this.container);
    }

  }

  _createHandle() {

    const handle = document.createElement('div');

    handle.className = 'groups-sidebar-resizer';
    handle.setAttribute('role', 'separator');
    handle.setAttribute('aria-orientation', 'vertical');
    handle.setAttribute('aria-label', 'Resize sidebar');
    handle.setAttribute('tabindex', '0');
    handle.setAttribute('title', 'Drag to resize — double click to reset');
    handle.innerHTML = '<span class="groups-sidebar-resizer__grip"></span>';

    this.container.insertBefore(handle, this.sidebar.nextSibling);
    this.handle = handle;

    handle.addEventListener('pointerdown', e => {

      // ignore anything but a primary press
      if(e.button !== 0) {
        return;
      }

      e.preventDefault();

      this.dragStartX = e.clientX;
      this.dragStartWidth = this.sidebar.getBoundingClientRect().width;

      handle.classList.add('is-dragging');
      document.body.classList.add('is-resizing-sidebar');

      try {
        handle.setPointerCapture(e.pointerId);
      } catch (err) {
        // setPointerCapture can throw if the pointer is already gone
      }

      handle.addEventListener('pointermove', this._onPointerMove);
      handle.addEventListener('pointerup', this._onPointerUp);
      handle.addEventListener('pointercancel', this._onPointerUp);

    });

    handle.addEventListener('dblclick', e => {
      e.preventDefault();
      this.setWidth(this.settings.defaultWidth);
    });

    handle.addEventListener('keydown', e => {

      let next = null;

      switch(e.key) {
        case 'ArrowLeft':
          next = this.sidebar.getBoundingClientRect().width - this.settings.step;
          break;
        case 'ArrowRight':
          next = this.sidebar.getBoundingClientRect().width + this.settings.step;
          break;
        case 'Home':
          next = this.settings.defaultWidth;
          break;
      }

      if(next === null) {
        return;
      }

      e.preventDefault();
      this.setWidth(next);

    });

  }

  _onPointerMove(e) {

    const next = this.dragStartWidth + (e.clientX - this.dragStartX);

    // paint at most once per frame while dragging
    if(this._raf) {
      cancelAnimationFrame(this._raf);
    }

    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      this.setWidth(next, { persist: false });
    });

  }

  _onPointerUp(e) {

    this.handle.removeEventListener('pointermove', this._onPointerMove);
    this.handle.removeEventListener('pointerup', this._onPointerUp);
    this.handle.removeEventListener('pointercancel', this._onPointerUp);

    try {
      this.handle.releasePointerCapture(e.pointerId);
    } catch (err) {
      // pointer may have already been released
    }

    if(this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }

    this.handle.classList.remove('is-dragging');
    document.body.classList.remove('is-resizing-sidebar');

    CacheInstance.save(this.settings.cacheKey, this.desiredWidth);

  }

  /**
   * Re-clamp on window resize so a wide sidebar doesn't squeeze the content
   * pane out of existence on smaller viewports — without forgetting the width
   * the user actually picked.
   */
  _onWindowResize() {
    this._scheduleApply();
  }

  _scheduleApply() {

    if(this._applyRaf) {
      return;
    }

    this._applyRaf = requestAnimationFrame(() => {
      this._applyRaf = null;
      this.applyWidth();
    });

  }

  /**
   * The widest the sidebar may currently be drawn. Falls back to the
   * configured maximum while the container has no measurable width (before
   * first layout), so an unmeasured container can't pin the sidebar shut.
   */
  maxWidth() {

    const containerWidth = this.container.getBoundingClientRect().width;

    if(!containerWidth) {
      return this.settings.maxWidth;
    }

    // the handle sits between the two panes, so it eats into the content too
    const handleWidth = this.handle ? this.handle.offsetWidth : 0;
    const handleMargin = this.handle
      ? (parseInt(getComputedStyle(this.handle).marginRight, 10) || 0)
      : 0;

    const available =
      containerWidth - this.settings.contentMinWidth - handleWidth - handleMargin;

    return Math.max(this.settings.minWidth, Math.min(this.settings.maxWidth, available));

  }

  /**
   * Draw the desired width, clamped to what currently fits.
   */
  applyWidth() {

    if(!this.handle) {
      return;
    }

    const applied = Math.round(
      Math.max(this.settings.minWidth, Math.min(this.maxWidth(), this.desiredWidth))
    );

    if(applied === this.appliedWidth) {
      return;
    }

    this.appliedWidth = applied;

    this.sidebar.style.flex = '0 0 auto';
    this.sidebar.style.width = `${applied}px`;
    this.sidebar.classList.toggle('is-narrow', applied < this.settings.narrowWidth);
    this.sidebar.classList.toggle('is-tiny', applied < this.settings.tinyWidth);

    this.handle.setAttribute('aria-valuenow', String(applied));
    this.handle.setAttribute('aria-valuemin', String(this.settings.minWidth));
    this.handle.setAttribute('aria-valuemax', String(Math.round(this.maxWidth())));

    document.dispatchEvent(new CustomEvent('sidebarResized', {
      detail: { width: applied, sidebar: this.sidebar }
    }));

  }

  /**
   * Record a new user-chosen width and draw it.
   */
  setWidth(width, { persist = true } = {}) {

    const requested = parseFloat(width);

    if(!Number.isFinite(requested)) {
      return;
    }

    this.desiredWidth = Math.round(
      Math.max(this.settings.minWidth, Math.min(this.maxWidth(), requested))
    );

    this.applyWidth();

    if(persist) {
      CacheInstance.save(this.settings.cacheKey, this.desiredWidth);
    }

  }

}

export default ResizableSidebar;
